import { uvsDisbursements, uvsUploadXlsx } from "api/admin";
import { SecondaryHeightLayout } from "hooks/viewport-dimensions";
import LabeledField from "providers/labeledField";
import { notifySimple } from "providers/toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";

import { formatDate, formatDateToZ } from "util/datelib";
import GenericCard from "widgets/cards/generic";
import GenericButton from "widgets/controls/buttons/generic";
import DateRangePickerField from "widgets/shared/inputs/dateRangePicker";
import UploadFile from "widgets/shared/uploadfile";

type LooseValue = Date | [Date, Date] | null;

type Disbursements = {
  link: string;
  name: string;
};

const Uvs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dateRange, setDateRange] = useState<LooseValue>(null);
  const [disbursements, setDisbursements] = useState<Disbursements[]>([]);

  const handleUploadFile = async (e: any) => {
    dispatch(setLoading(true));
    const { isLoading, error } = await uvsUploadXlsx(e);
    dispatch(setLoading(isLoading));

    // Check if the API response contains a URL to download
    if (!error) {
      notifySimple(t("notification.success"), "success");
    } else {
      notifySimple(t("notification.error"), "error");
    }
  };

  const handleDownloadFile = (item: Disbursements) => {
    window.open(item.link, "_blank"); // Open the URL in a new tab
  };

  const handleGetDisbursements = async () => {
    if (dateRange && Array.isArray(dateRange)) {
      const from = formatDateToZ(new Date(dateRange[0]), false, "day", 2); // Start of selected date
      const to = formatDateToZ(new Date(dateRange[1]), true, "day", 2); // End of selected date

      dispatch(setLoading(true));

      const { data, isLoading } = await uvsDisbursements(from, to);
      dispatch(setLoading(isLoading));

      // Check if the API response contains a URL to download
      if (data && data.data) {
        setDisbursements(data.files);
      }
    } else {
      notifySimple(t("notification.invalid.date.range"), "error");
    }
  };

  return (
    <SecondaryHeightLayout>
      <GenericCard extra="h-full" title={t("support.uvs.uploads")}>
        <div className="flex flex-col gap-6">
          <LabeledField
            label={t("support.uvs.disbursements.uploads")}
            id={"uploads"}
            textColor="secondary"
          >
            <UploadFile
              id="upload"
              onChange={handleUploadFile}
              color="secondary"
            />
          </LabeledField>
          <LabeledField
            label={t("support.uvs.disbursements.downloads")}
            id={"downloads"}
            className="text-txt-lg"
            textColor="secondary"
          >
            <div className="flex items-center gap-4">
              <div>
                <DateRangePickerField
                  id={"daterange"}
                  onChange={(val) => {
                    setDateRange(val); // Set new date and trigger useEffect
                  }}
                  maxDate={new Date()}
                  sizes={"md"}
                  color="secondary"
                />
              </div>

              {dateRange && Array.isArray(dateRange) && (
                <label className="text-text-800">{`${formatDate(
                  dateRange[0],
                  "long"
                )} - ${formatDate(dateRange[1], "long")}`}</label>
              )}
            </div>
            <GenericButton
              text={t("button.action.download")}
              onClick={handleGetDisbursements}
              color="secondary"
              extra="mt-2"
            />
            <div className="grid grid-cols-3 gap-2">
              {disbursements.map((val, index) => {
                return (
                  <>
                    <GenericButton
                      size="md"
                      variant="outline"
                      text={val.name}
                      onClick={() => handleDownloadFile(val)}
                    />
                  </>
                );
              })}
            </div>
          </LabeledField>
        </div>
      </GenericCard>
    </SecondaryHeightLayout>
  );
};

export default Uvs;
